import { UserProfile } from '../../generated/auth/v1/auth_pb';

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'netgrocer_access_token',
  REFRESH_TOKEN: 'netgrocer_refresh_token',
  TOKEN_EXPIRY: 'netgrocer_token_expiry',
  USER: 'netgrocer_user',
  OAUTH_STATE: 'netgrocer_oauth_state',
} as const;

export interface StoredAuthData {
  accessToken: string;
  refreshToken: string;
  tokenExpiresAt: number;
  user: UserProfile;
}

/**
 * Save authentication data to localStorage
 */
export const saveAuthData = (data: StoredAuthData): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, data.tokenExpiresAt.toString());
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
  } catch (error) {
    console.error('Failed to save auth data to localStorage:', error);
  }
};

/**
 * Load authentication data from localStorage
 */
export const loadAuthData = (): StoredAuthData | null => {
  try {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const tokenExpiryStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);

    if (!accessToken || !refreshToken || !tokenExpiryStr || !userStr) {
      return null;
    }

    const tokenExpiresAt = parseInt(tokenExpiryStr, 10);
    if (isNaN(tokenExpiresAt)) {
      return null;
    }

    const user = JSON.parse(userStr) as UserProfile;

    return {
      accessToken,
      refreshToken,
      tokenExpiresAt,
      user,
    };
  } catch (error) {
    console.error('Failed to load auth data from localStorage:', error);
    return null;
  }
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
    localStorage.removeItem(STORAGE_KEYS.USER);
  } catch (error) {
    console.error('Failed to clear auth data from localStorage:', error);
  }
};

/**
 * Save OAuth state parameter for CSRF protection
 */
export const saveOAuthState = (state: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.OAUTH_STATE, state);
  } catch (error) {
    console.error('Failed to save OAuth state:', error);
  }
};

/**
 * Get OAuth state parameter
 */
export const getOAuthState = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.OAUTH_STATE);
  } catch (error) {
    console.error('Failed to get OAuth state:', error);
    return null;
  }
};

/**
 * Clear OAuth state parameter
 */
export const clearOAuthState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.OAUTH_STATE);
  } catch (error) {
    console.error('Failed to clear OAuth state:', error);
  }
};
